import React, { useMemo } from "react"
import PropTypes from "prop-types"
import {
    Box,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material"
import RefreshIcon from "@mui/icons-material/Refresh"
import SelectComponent from "src/v2/components/select/select-component"
import ButtonWithIcon from "src/v2/components/Button/Button"
import { formatFixedDecimals, formatDecimalSeparator } from "src/v2/utils/formatter/format-number"
import { buildFinancialSeries, formatFinancialPeriod } from "./company-financial-data"

export const formatUnitValue = (formattedValue, unit) => {
    const normalizedUnit = String(unit ?? "").trim()

    if (!normalizedUnit) return formattedValue

    if (normalizedUnit === "%") {
        return formattedValue.endsWith("%") ? formattedValue : `${formattedValue}%`
    }

    if (normalizedUnit.startsWith("R$")) return `R$ ${formattedValue}`

    return `${formattedValue} ${normalizedUnit}`
}

const formatValue = (value, fixedDecimals, unit = "") => {
    if (value === null || value === undefined || value === "") return "-"

    if (fixedDecimals !== undefined) {
        const formatted = formatFixedDecimals(value, fixedDecimals)
        if (formatted === "-") return "-"
        return formatUnitValue(formatted, unit)
    }

    const formatted = formatDecimalSeparator(value)
    if (formatted === "-") return "-"
    return formatUnitValue(formatted, unit)
}

const FINANCIAL_COLUMN_WIDTHS = {
    metric: 280,
    unit: 160,
    period: 180,
}

const getFinancialTableMinWidth = (periodCount) => (
    FINANCIAL_COLUMN_WIDTHS.metric
    + FINANCIAL_COLUMN_WIDTHS.unit
    + (periodCount * FINANCIAL_COLUMN_WIDTHS.period)
)

const FinancialTableColumns = ({ periods }) => (
    <colgroup>
        <col style={{ width: `${FINANCIAL_COLUMN_WIDTHS.metric}px` }} />
        <col style={{ width: `${FINANCIAL_COLUMN_WIDTHS.unit}px` }} />
        {periods.map((period) => (
            <col key={period} style={{ width: `${FINANCIAL_COLUMN_WIDTHS.period}px` }} />
        ))}
    </colgroup>
)

FinancialTableColumns.propTypes = {
    periods: PropTypes.arrayOf(PropTypes.string).isRequired,
}

const headerCellSx = {
    px: 1.75,
    py: 2,
    color: "common.white",
    fontFamily: "'Titillium Web', sans-serif",
    fontStyle: "normal",
    fontWeight: 700,
    fontSize: "0.875rem",
    lineHeight: "1.25rem",
    textTransform: "uppercase",
    whiteSpace: "nowrap",
    borderBottom: "none",
    backgroundColor: "transparent",
}

const bodyCellSx = {
    px: 1.75,
    py: 2.5,
    color: "common.white",
    fontFamily: "'Roboto', sans-serif",
    fontStyle: "normal",
    fontWeight: 400,
    fontSize: "0.875rem",
    lineHeight: "1rem",
    whiteSpace: "nowrap",
    borderBottom: "none",
    backgroundColor: "transparent",
}

const FinancialTable = ({
    ariaLabel,
    periods,
    rowName,
    unit,
    values,
    formatVal,
}) => (
    <TableContainer
        sx={{
            overflow: "visible",
            backgroundColor: "transparent",
        }}
    >
        <Table
            aria-label={ariaLabel}
            sx={{
                width: "100%",
                tableLayout: "fixed",
                borderCollapse: "separate",
                borderSpacing: 0,
            }}
        >
            <FinancialTableColumns periods={periods} />
            <TableHead>
                <TableRow>
                    <TableCell align="left" sx={headerCellSx}>
                        Métrica
                    </TableCell>
                    <TableCell align="left" sx={headerCellSx}>
                        Unidade
                    </TableCell>
                    {periods.map((period, index) => (
                        <TableCell
                            key={period}
                            align="right"
                            sx={headerCellSx}
                        >
                            <Box component="span" sx={{ display: "inline-flex", flexDirection: "column", alignItems: "flex-end" }}>
                                <Box component="span">{formatFinancialPeriod(period)}</Box>
                                {index === 0 && (
                                    <Typography
                                        component="span"
                                        variant="caption"
                                        sx={{ color: "primary.white70", fontWeight: 400, textTransform: "none" }}
                                    >
                                        Mais recente
                                    </Typography>
                                )}
                            </Box>
                        </TableCell>
                    ))}
                </TableRow>
            </TableHead>
            <TableBody>
                <TableRow sx={{ backgroundColor: "input.background.white05" }}>
                    <TableCell
                        component="th"
                        scope="row"
                        align="left"
                        sx={bodyCellSx}
                    >
                        {rowName}
                    </TableCell>
                    <TableCell align="left" sx={bodyCellSx}>
                        {unit}
                    </TableCell>
                    {periods.map((period, index) => (
                        <TableCell
                            key={period}
                            align="right"
                            sx={{
                                ...bodyCellSx,
                                fontVariantNumeric: "tabular-nums",
                            }}
                        >
                            {formatVal(values[index])}
                        </TableCell>
                    ))}
                </TableRow>
            </TableBody>
        </Table>
    </TableContainer>
)

FinancialTable.propTypes = {
    ariaLabel: PropTypes.string.isRequired,
    periods: PropTypes.arrayOf(PropTypes.string).isRequired,
    rowName: PropTypes.string.isRequired,
    unit: PropTypes.string.isRequired,
    values: PropTypes.array.isRequired,
    formatVal: PropTypes.func.isRequired,
}

const CompanyFinancialSection = ({ financial }) => {
    const {
        status,
        error,
        warning,
        failedGroups = [],
        availableGroups,
        selectedGroup,
        selectGroup,
        dataByGroup,
        reload,
    } = financial

    const selectedData = dataByGroup[selectedGroup]
    const { periods, balanceRows, indicatorRows } = useMemo(
        () => buildFinancialSeries(selectedData?.records ?? []),
        [selectedData?.records]
    )

    if (status === "idle") {
        return (
            <Typography color="common.white" sx={{ py: 2 }}>
                Selecione uma empresa para consultar os indicadores financeiros.
            </Typography>
        )
    }

    if (status === "loading") {
        return (
            <Box role="status" aria-label="Carregando indicadores financeiros" sx={{ display: "flex", flexDirection: "column", gap: 3, py: 1 }}>
                <Box>
                    <Skeleton variant="text" width={180} height={28} sx={{ backgroundColor: "rgba(255, 255, 255, 0.1)", mb: 1.5 }} />
                    <Skeleton variant="rectangular" height={88} sx={{ backgroundColor: "rgba(255, 255, 255, 0.05)", borderRadius: 1 }} />
                </Box>
                <Box>
                    <Skeleton variant="text" width={180} height={28} sx={{ backgroundColor: "rgba(255, 255, 255, 0.1)", mb: 1.5 }} />
                    <Skeleton variant="rectangular" height={88} sx={{ backgroundColor: "rgba(255, 255, 255, 0.05)", borderRadius: 1 }} />
                </Box>
            </Box>
        )
    }

    if (status === "empty") {
        return (
            <Box sx={{ minHeight: 220, display: "grid", placeItems: "center", textAlign: "center", px: 2 }}>
                <Typography color="common.white">
                    Não há indicadores financeiros e balanço patrimonial disponíveis para esta empresa.
                </Typography>
            </Box>
        )
    }

    if (status === "error") {
        return (
            <Box sx={{ minHeight: 220, display: "grid", placeItems: "center", gap: 2 }}>
                <Typography role="alert" color="common.white">
                    {error}
                </Typography>
                <ButtonWithIcon icon={<RefreshIcon />} onClick={reload}>
                    TENTAR NOVAMENTE
                </ButtonWithIcon>
            </Box>
        )
    }

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {warning && failedGroups.length > 0 && (
                <Box role="alert" sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
                    <Typography color="common.white">{warning}</Typography>
                    <ButtonWithIcon icon={<RefreshIcon />} onClick={reload}>
                        TENTAR NOVAMENTE
                    </ButtonWithIcon>
                </Box>
            )}

            {availableGroups.length > 0 && (
                <Box sx={{ maxWidth: 280 }}>
                    <SelectComponent
                        label="Grupo de balanço"
                        value={selectedGroup}
                        onChange={(event) => selectGroup(event.target.value)}
                        options={availableGroups.map((group) => ({ value: group, label: group }))}
                        disabled={availableGroups.length === 1}
                    />
                </Box>
            )}

            <Box
                role="region"
                aria-label="Tabelas financeiras"
                tabIndex={0}
                sx={{
                    overflowX: "auto",
                    maxWidth: "100%",
                    "&:focus-visible": {
                        outline: (theme) => `2px solid ${theme.palette.common.white}`,
                        outlineOffset: 2,
                    },
                }}
            >
                <Box
                    sx={{
                        minWidth: `${getFinancialTableMinWidth(periods.length)}px`,
                        display: "flex",
                        flexDirection: "column",
                        gap: 3,
                    }}
                >
                    <Box component="section" aria-labelledby="balanco-patrimonial-heading">
                        <Typography
                            id="balanco-patrimonial-heading"
                            variant="body1"
                            component="h3"
                            color="common.white"
                            sx={{ fontWeight: 400, mb: 1.5 }}
                        >
                            Balanço Patrimonial
                        </Typography>
                        {balanceRows.length > 0 ? (
                            <FinancialTable
                                ariaLabel="Balanço Patrimonial"
                                periods={periods}
                                rowName="Ativo Total"
                                unit={selectedData?.unit || "R$ MM"}
                                values={balanceRows[0]?.values ?? []}
                                formatVal={(val) => formatValue(val, 2, selectedData?.unit || "R$")}
                            />
                        ) : (
                            <Typography sx={{ color: (theme) => theme?.palette?.primary?.white70 || "rgba(255, 255, 255, 0.7)", py: 1 }}>
                                Não há dados de Balanço Patrimonial disponíveis para este grupo.
                            </Typography>
                        )}
                    </Box>

                    <Box component="section" aria-labelledby="indicadores-financeiros-heading">
                        <Typography
                            id="indicadores-financeiros-heading"
                            variant="body1"
                            component="h3"
                            color="common.white"
                            sx={{ fontWeight: 400, mb: 1.5 }}
                        >
                            Indicadores Financeiros
                        </Typography>
                        {indicatorRows.length > 0 ? (
                            <FinancialTable
                                ariaLabel="Indicadores Financeiros"
                                periods={periods}
                                rowName="ROA"
                                unit="%"
                                values={indicatorRows[0]?.values ?? []}
                                formatVal={(val) => formatValue(val, undefined, "%")}
                            />
                        ) : (
                            <Typography sx={{ color: (theme) => theme?.palette?.primary?.white70 || "rgba(255, 255, 255, 0.7)", py: 1 }}>
                                Não há dados de Indicadores Financeiros disponíveis para este grupo.
                            </Typography>
                        )}
                    </Box>
                </Box>
            </Box>
        </Box>
    )
}

CompanyFinancialSection.propTypes = {
    financial: PropTypes.shape({
        status: PropTypes.string.isRequired,
        error: PropTypes.string,
        warning: PropTypes.string,
        failedGroups: PropTypes.arrayOf(PropTypes.string),
        availableGroups: PropTypes.arrayOf(PropTypes.string).isRequired,
        selectedGroup: PropTypes.string.isRequired,
        selectGroup: PropTypes.func.isRequired,
        dataByGroup: PropTypes.object.isRequired,
        reload: PropTypes.func.isRequired,
    }).isRequired,
}

export default CompanyFinancialSection
